import { Component, input, OnDestroy, OnInit, signal } from '@angular/core';
import { SubHeaderService } from '../../../store/subheader/subheader.service';
import { ViewService } from '../../../store/view/views.service';
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormService } from '../../../store/form/form.service';
import { AccountingService } from '../../../store/accounting/accounting.service';
import { ModalService } from '../../../service/modal.service';
import { ApplicationFeeRuleService } from '../../../store/application-fee-rule/application-fee-rule.service';
import { ShowSignValueTypePipe } from '../../../pipe/show-sign-value-type.pipe';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DataType,
  DropdownStyle,
  SourceEnum,
} from '../../../store/typeahead/types';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  take,
} from 'rxjs';
import { Entity, View } from '../../../store/view/types';
import {
  AccountModel,
  AccountMoveInstanceParams,
  AccountMoveInvoiceExtendedRepaymentChoice,
  AccountMoveModel,
  AccountMovePeriodModel,
  APPLICATION_FEES_ID,
  INSURANCE_CLAIM_FEES_ID,
  InvoiceExtendedFormModel,
  InvoiceFormModel,
  PaymentFormModel,
  RECURRING_FEES_ID,
} from '../../../store/accounting/types';
import { ApplicationFeeRuleModel } from '../../../store/application-fee-rule/types';
import { GenericApiCall } from '../../../../common/store/types';
import { AmountType } from '../../../store/commissions/commission-plan-rule/types';
import { MAX_DATE_MASK_SAFE_GUARD } from '../../../constants/date.constants';
import { getAmountSign } from '../../../helper/amounthelper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debugLog, filterTrue } from '../../../pipe/rxjs/operators';
import { formatDate } from '../../../helper/datehelper';
import { removeValidators, setValidators } from '../../../helper/formhelper';
import {
  ModalComponent,
  ModalComponentKey,
} from 'src/app/shared/service/modal-component';

@UntilDestroy()
@ModalComponent(ModalComponentKey.AccountMoveAddInvoiceExtended)
@Component({
  selector: 'app-account-move-add-invoice-extended-content',
  standalone: false,
  templateUrl: './account-move-add-invoice-extended-content.component.html',
  styleUrl: './account-move-add-invoice-extended-content.component.scss',
})
export class AccountMoveAddInvoiceExtendedContentComponent
  implements OnInit, OnDestroy
{
  readonly modalRandomIdentifier = input.required<number>();
  readonly contractId = input.required<number>();
  /** Renseigné quand la modale est ouverte depuis l'onglet quittances d'un sinistre. */
  readonly insuranceClaimId = input<number>();
  readonly isClient = input<boolean>(true);
  /** Absent quand la ligne de produit du contrat ne porte pas de compagnie d'assurance. */
  readonly contractInsuranceCompanyContactId = input<number>();
  readonly accountMoveId = input<number>();
  readonly source = input<SourceEnum>(SourceEnum.Component);

  view$: Observable<View>;
  invoiceForm: FormGroup;
  accountMoveInstance$: Observable<AccountMoveModel>;
  accountMovePeriodsModels$: Observable<AccountMovePeriodModel[]>;
  insuranceCompanyAccount$: Observable<AccountModel>;
  insuranceCompanyAccountId: number = 0;
  applicationFeeRuleModel$: Observable<ApplicationFeeRuleModel>;

  accountMoveModel$: Observable<AccountMoveModel>;
  isEdit: boolean = false;
  accountMoveModel: AccountMoveModel;

  apiCall: GenericApiCall<any, any>;
  isRecurringFees = signal<boolean>(false);
  isOverPaid = signal<boolean>(false);
  accountToDomain: string = '';

  defaultAmountSign: AmountType = 'Monetary';
  unitPricePlaceholder: string;
  unitPriceSuffix: string;
  saveValue: { minValue: ValidatorFn; maxValue: ValidatorFn } | undefined;

  protected readonly DataType = DataType;
  protected readonly DropdownStyle = DropdownStyle;
  protected readonly Entity = Entity;

  maxDateMaskSafeGuard: string = MAX_DATE_MASK_SAFE_GUARD;

  constructor(
    private readonly subHeaderService: SubHeaderService,
    private readonly viewService: ViewService,
    private readonly formBuilder: FormBuilder,
    private readonly formService: FormService,
    private readonly accountingService: AccountingService,
    private readonly dynamicService: ModalService,
    private readonly modalService: NgbModal,
    private readonly applicationFeeRuleService: ApplicationFeeRuleService,
    private readonly showSignValueType: ShowSignValueTypePipe,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.accountMovePeriodsModels$ = this.accountingService.getPeriods.value$;
    this.accountMoveInstance$ = this.accountingService.createInstance.value$;
    this.insuranceCompanyAccount$ =
      this.accountingService.getAccountByContactId.value$;
    this.applicationFeeRuleModel$ =
      this.applicationFeeRuleService.getByContractId.value$;
    this.accountMoveModel$ = this.accountingService.getById.value$;
  }

  ngOnInit(): void {
    this.isEdit = !!this.accountMoveId();
    this.unitPricePlaceholder = this.unitPriceSuffix = getAmountSign(
      this.defaultAmountSign,
    );

    const insuranceCompanyContactId = this.contractInsuranceCompanyContactId();

    const brokerDomain: string = '(accountType=2,active=1)';
    //sans compagnie, on n'a pas de clause à ajouter : on retombe sur le seul domaine courtier.
    const insuranceCompanyDomain: string = insuranceCompanyContactId
      ? '|(contactId=' + insuranceCompanyContactId + ',active=1,accountType=1)'
      : '';
    this.accountToDomain = brokerDomain + insuranceCompanyDomain;
    this.isRecurringFees.set(false);
    this.isOverPaid.set(false);
    if (insuranceCompanyContactId) {
      this.accountingService.getAccountByContactId.call(
        insuranceCompanyContactId.toString(),
      );
    }

    if (this.isEdit) {
      this.apiCall = this.accountingService.getById;
      this.accountingService.getById.call(this.accountMoveId()!.toString());
    } else {
      this.apiCall = this.accountingService.getPeriods;
      let accountMoveInstanceParams: AccountMoveInstanceParams = {
        contractId: this.contractId(),
        insuranceClaimId: this.insuranceClaimId(),
      };

      accountMoveInstanceParams.moveType = this.isClient()
        ? 'CustomerInvoice'
        : 'VendorInvoice';

      this.accountingService.getPeriods.call(this.contractId());
      this.accountingService.createInstance.call(accountMoveInstanceParams);
    }

    this.invoiceForm = this.formBuilder.group({
      accountMoveTypeId: [undefined],
      debitAccountId: [undefined, [Validators.required]],
      creditAccountId: [undefined, [Validators.required]],
      invoiceAmount: [
        undefined,
        [Validators.required, Validators.minLength(1)],
      ],
      invoiceNumber: [],
      accountMovePeriod: [],
      periodStartDate: [],
      periodEndDate: [],
      needToCreateRepayment: [false],
      paymentChoice: [true],
      paymentMode: [undefined, [Validators.required]],
      paymentDate: [
        formatDate(new Date(), 'ToInverseSqlDate'),
        [Validators.required],
      ],
      paymentReference: [''],
      paymentProofFile: [],

      accountMoveName: [undefined, [Validators.required]],
    });

    this.accountMoveInstance$
      .pipe(
        filter((x) => !!x && !this.isEdit),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.invoiceForm.patchValue({
          debitAccountId: this.isClient() ? x.debitAccountId : undefined,
          creditAccountId: this.isClient() ? undefined : x.creditAccountId,
        });
        // if (!this.insuranceClaimId())
        //   this.invoiceForm.patchValue({
        //     name: x.name,
        //   });

        //Sur un sinistre, l'instance porte déjà le montant par défaut du type de sinistre
        //(ClaimType.DefaultFeeAmount, posé par le backend dans CreateEntity) : le front n'a donc pas
        //à connaître la règle. On pousse une chaîne, comme la valeur que produit le masque, pour que
        //toFormData n'envoie pas un décimal à virgule que le backend ne saurait pas binder.
        const defaultAmount = x.accountMoveLines?.[0]?.unitPrice;
        if (
          this.insuranceClaimId() &&
          defaultAmount != null &&
          defaultAmount != 0
        ) {
          this.invoiceForm
            .get('invoiceAmount')
            ?.setValue(defaultAmount.toString());
        }
      });

    this.accountMoveModel$
      .pipe(
        filter((x) => !!x && this.isEdit),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        // this.accountMoveModel = x;
        // this.invoiceForm.patchValue({
        //   accountMoveName: x.name,
        //   accountMovePaymentStatus: x.paymentStatus,
        //   contractId: x.contractId,
        //   directionSign: x.directionSign,
        //   status: x.status,
        //   moveType: x.moveType,
        //   autoPost: x.autoPost,
        //   dueDate: x.deliveryDate,
        //   salesPersonId: x.salesPersonId,
        //   accountMoveStatus: x.status,
        //   creationDate: x.creationDate,
        //   currencyId: x.accountMoveLines[0].currencyId,
        //   sequence: x.accountMoveLines[0].sequence,
        //   quantity: x.accountMoveLines[0].quantity,
        //   unitPrice: x.accountMoveLines[0].unitPrice,
        //   discount: x.accountMoveLines[0].discount,
        //   debit: x.accountMoveLines[0].debit,
        //   credit: x.accountMoveLines[0].credit,
        //   accountMoveLinePaymentStatus: x.accountMoveLines[0].paymentStatus,
        //   contactId: x.contactId,
        //   debitAccountId: x.debitAccountId,
        //   creditAccountId: x.creditAccountId,
        // });
      });

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'invoiceExtendedCreate') this.onSubmit();
        this.subHeaderService.setTriggerAction('');
      });

    this.invoiceForm.controls['accountMovePeriod']?.valueChanges
      .pipe(debounceTime(150))
      .subscribe((accountMovePeriodName) => {
        this.accountMovePeriodsModels$
          .pipe(filterTrue(), untilDestroyed(this))
          .subscribe((periods) => {
            const period = periods.find((x) => x.name == accountMovePeriodName);
            this.invoiceForm.patchValue({
              periodStartDate: formatDate(
                period?.startDate!,
                'ToInverseSqlDate',
              ),
              periodEndDate: formatDate(period?.endDate!, 'ToInverseSqlDate'),
            });
          });
      });

    this.invoiceForm.controls['accountMoveName']?.valueChanges
      .pipe(debounceTime(150))
      .subscribe((accountMoveName: string) => {
        this.invoiceForm.patchValue({
          accountMoveTypeId: undefined,
          periodStartDate: undefined,
          periodEndDate: undefined,
        });

        //Sur un sinistre le type est forcé et le typeahead est en lecture seule : aucune des branches
        //contrat ne peut s'appliquer. On discrimine sur `insuranceClaimId` et pas sur le libellé, qui
        //est la description localisée de l'enum et change donc avec la culture de la requête.
        if (this.insuranceClaimId()) {
          this.invoiceForm.patchValue({
            accountMoveTypeId: INSURANCE_CLAIM_FEES_ID,
          });
          this.isOverPaid.set(false);
          this.isRecurringFees.set(false);
          removeValidators(
            this.invoiceForm.get(['periodStartDate'] as const),
            Validators.required,
          );
          removeValidators(
            this.invoiceForm.get(['periodEndDate'] as const),
            Validators.required,
          );
          this.accountToDomain = brokerDomain + insuranceCompanyDomain;
          return;
        }

        if (accountMoveName === 'Echéance') {
          this.invoiceForm.patchValue({ accountMoveTypeId: RECURRING_FEES_ID });

          this.isRecurringFees.set(true);
          setValidators(
            this.invoiceForm.get(['periodStartDate'] as const),
            Validators.required,
          );
          setValidators(
            this.invoiceForm.get(['periodEndDate'] as const),
            Validators.required,
          );
          this.accountToDomain = brokerDomain;
          this.applicationFeeRuleService.getByContractId.reset();
          if (
            this.invoiceForm.get(['creditAccountId'])?.value ===
            this.insuranceCompanyAccountId
          ) {
            this.invoiceForm.patchValue({
              creditAccountId: undefined,
            });
          }
        } else {
          if (
            accountMoveName === 'Frais de dossier' ||
            accountMoveName === 'Frais de courtage'
          ) {
            this.invoiceForm.patchValue({
              accountMoveTypeId: APPLICATION_FEES_ID,
            });
          } else {
            if (accountMoveName?.toLowerCase().includes('trop')) {
              this.isOverPaid.set(true);
            } else {
              this.isOverPaid.set(false);
            }
          }
          this.isRecurringFees.set(false);
          removeValidators(
            this.invoiceForm.get(['periodStartDate'] as const),
            Validators.required,
          );
          removeValidators(
            this.invoiceForm.get(['periodEndDate'] as const),
            Validators.required,
          );
          if (
            !accountMoveName ||
            accountMoveName === 'Frais de dossier' ||
            accountMoveName === 'Frais de courtage'
          ) {
            this.accountToDomain = brokerDomain + insuranceCompanyDomain;

            if (
              accountMoveName === 'Frais de dossier' ||
              accountMoveName === 'Frais de courtage'
            ) {
              if (
                this.invoiceForm.get(['creditAccountId'])?.value ===
                this.insuranceCompanyAccountId
              ) {
                this.applicationFeeRuleService.getByContractId.call({
                  id: this.contractId(),
                  type: 'Vendor',
                });
              } else {
                if (!this.invoiceForm.get(['creditAccountId'])?.value) {
                  this.applicationFeeRuleService.getByContractId.reset();
                } else {
                  this.applicationFeeRuleService.getByContractId.call({
                    id: this.contractId(),
                    type: 'Customer',
                  });
                }
              }
            }
          } else {
            this.accountToDomain = brokerDomain;
            this.applicationFeeRuleService.getByContractId.reset();
            if (
              this.invoiceForm.get(['creditAccountId'])?.value ===
              this.insuranceCompanyAccountId
            ) {
              this.invoiceForm.patchValue({
                creditAccountId: undefined,
              });
            }
          }
        }
      });

    this.invoiceForm
      .get('creditAccountId')
      ?.valueChanges?.pipe(
        distinctUntilChanged(),
        //la bascule automatique vers les frais de courtage ne concerne que les quittances de contrat.
        filter(() => !this.insuranceClaimId()),
        untilDestroyed(this),
      )
      ?.subscribe((value) => {
        if (!!value) {
          if (
            !!this.insuranceCompanyAccountId &&
            value === this.insuranceCompanyAccountId
          ) {
            // this.invoiceForm.patchValue({
            //   repaymentChoice:
            //     AccountMoveInvoiceExtendedRepaymentChoice.WithRepaymentBroker,
            // });
            if (
              this.invoiceForm.get('accountMoveName')?.value !==
                'Frais de dossier' ||
              this.invoiceForm.get('accountMoveName')?.value !==
                'Frais de courtage'
            ) {
              this.invoiceForm.patchValue({
                accountMoveName: 'Frais de courtage',
              });
            }
            this.applicationFeeRuleService.getByContractId.call({
              id: this.contractId(),
              type: 'Vendor',
            });
          } else {
            // this.invoiceForm.patchValue({
            //   repaymentChoice:
            //     AccountMoveInvoiceExtendedRepaymentChoice.WithRepaymentCompany,
            // });
            if (
              this.invoiceForm.get('accountMoveName')?.value ===
                'Frais de dossier' ||
              this.invoiceForm.get('accountMoveName')?.value ===
                'Frais de courtage'
            ) {
              this.applicationFeeRuleService.getByContractId.call({
                id: this.contractId(),
                type: 'Customer',
              });
            } else {
              this.applicationFeeRuleService.getByContractId.reset();
            }
          }
        } else {
          this.applicationFeeRuleService.getByContractId.reset();
          // this.invoiceForm.patchValue({
          //   repaymentChoice:
          //     AccountMoveInvoiceExtendedRepaymentChoice.NoRepayment,
          // });
        }
      });

    this.insuranceCompanyAccount$
      .pipe(filterTrue(), take(1))
      .subscribe((account: AccountModel) => {
        this.insuranceCompanyAccountId = 0;
        if (account) {
          this.insuranceCompanyAccountId = account.id;
        }
      });

    this.applicationFeeRuleModel$
      .pipe(untilDestroyed(this))
      .subscribe((applicationFeeRule) => {
        if (applicationFeeRule) {
          this.invoiceForm.patchValue({
            invoiceAmount: applicationFeeRule.defaultValue,
          });

          //save min and max
          this.saveValue = {
            minValue: Validators.min(applicationFeeRule.minimumValue),
            maxValue: Validators.max(applicationFeeRule.maximumValue),
          };

          //set min
          setValidators(
            this.invoiceForm.get(['invoiceAmount'] as const),
            this.saveValue.minValue,
          );

          //set max
          setValidators(
            this.invoiceForm.get(['invoiceAmount'] as const),
            this.saveValue.maxValue,
          );

          //update suffix
          this.unitPriceSuffix = getAmountSign(
            this.defaultAmountSign,
            applicationFeeRule.amountType,
          );

          //update placeholder
          this.unitPricePlaceholder =
            this.showSignValueType.transform(
              applicationFeeRule.minimumValue,
              applicationFeeRule.amountType,
            ) +
            ' - ' +
            this.showSignValueType.transform(
              applicationFeeRule.maximumValue,
              applicationFeeRule.amountType,
            );
        } else {
          this.unitPricePlaceholder = this.unitPriceSuffix = getAmountSign(
            this.defaultAmountSign,
          );

          //no rule
          if (this.saveValue) {
            //unset min
            removeValidators(
              this.invoiceForm.get(['invoiceAmount'] as const),
              this.saveValue.minValue,
            );

            //unset max
            removeValidators(
              this.invoiceForm.get(['invoiceAmount'] as const),
              this.saveValue.maxValue,
            );
          }
          //reset errors
          this.invoiceForm.get(['invoiceAmount'] as const)?.setErrors(null);

          if (this.invoiceForm.dirty) {
            //remove value
            this.invoiceForm.patchValue({
              invoiceAmount: undefined,
            });

            //reset saveValue
            this.saveValue = undefined;
          }
        }

        if (
          this.invoiceForm.get(['invoiceAmount'] as const)?.value ===
            undefined ||
          this.invoiceForm.get(['invoiceAmount'] as const)?.value === null
        )
          this.invoiceForm
            .get(['invoiceAmount'] as const)
            ?.setErrors(Validators.required);
      });

    this.invoiceForm
      .get('paymentMode')
      ?.valueChanges?.pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result == 'Cheque') {
          setValidators(
            this.invoiceForm.get(['paymentReference'] as const),
            Validators.required,
          );
        } else {
          removeValidators(
            this.invoiceForm.get(['paymentReference'] as const),
            Validators.required,
          );
        }
      });

    this.invoiceForm
      .get('paymentChoice')
      ?.valueChanges?.pipe(distinctUntilChanged(), untilDestroyed(this))
      ?.subscribe((value: boolean) => {
        if (value) {
          setValidators(
            this.invoiceForm.get(['paymentMode'] as const),
            Validators.required,
          );
          setValidators(
            this.invoiceForm.get(['paymentDate'] as const),
            Validators.required,
          );
        } else {
          removeValidators(
            this.invoiceForm.get(['paymentMode'] as const),
            Validators.required,
          );
          removeValidators(
            this.invoiceForm.get(['paymentDate'] as const),
            Validators.required,
          );
          removeValidators(
            this.invoiceForm.get(['paymentReference'] as const),
            Validators.required,
          );

          this.invoiceForm.patchValue({
            paymentMode: undefined,
            paymentDate: formatDate(new Date(), 'ToInverseSqlDate'),
            paymentReference: '',
            paymentProofFile: undefined,
          });
        }
      });
  }

  onSubmit() {
    if (this.invoiceForm.invalid) {
      this.invoiceForm.markAllAsTouched();
      this.formService.setEntityErrors('INVOICE_EXTENDED');
      this.formService.countErrors(this.invoiceForm, true);
      return;
    }

    let name: string = this.invoiceForm.value.accountMoveName;
    if (this.isRecurringFees()) {
      if (this.invoiceForm.value.accountMovePeriod != null) {
        name = `${this.invoiceForm.value.accountMoveName} ${this.invoiceForm.value.accountMovePeriod}`;
      } else {
        name = this.invoiceForm.value.accountMoveName;
      }
    }
    const invoiceFormModel: InvoiceFormModel = {
      invoiceName: name,
      accountMoveTypeId: this.invoiceForm.value.accountMoveTypeId,
      debitAccountId: this.invoiceForm.value.debitAccountId,
      creditAccountId: this.invoiceForm.value.creditAccountId,
      invoiceAmount: this.invoiceForm.value.invoiceAmount,
      invoiceNumber: this.invoiceForm.value.invoiceNumber,
      periodStartDate: this.invoiceForm.value.periodStartDate,
      periodEndDate: this.invoiceForm.value.periodEndDate,
    };

    const paymentFormModel: PaymentFormModel = {
      paymentMode: this.invoiceForm.value.paymentMode,
      paymentDate: this.invoiceForm.value.paymentDate,
      reference: this.invoiceForm.value.paymentReference,
      proofFile: this.invoiceForm.value.paymentProofFile,
    };

    const invoiceExtendedFormModel: InvoiceExtendedFormModel = {
      contractId: this.contractId(),
      insuranceClaimId: this.insuranceClaimId(),
      invoiceFormModel: invoiceFormModel,
      paymentFormModel: !!this.invoiceForm.value.paymentChoice
        ? paymentFormModel
        : null,
      needToCreateRepayment: this.invoiceForm.value.needToCreateRepayment,
    };

    // let result$: Observable<number>;
    // if (this.isEdit) {
    // let accountMove = deepCopy(this.accountMoveModel);
    //
    // accountMove.accountMoveLines[0].unitPrice =
    //   this.invoiceForm.value.unitPrice;
    // accountMove.accountMoveLines[0].credit = this.invoiceForm.value.unitPrice;
    // result$ = this.accountingService.update.call(accountMove);
    // } else {

    let result$ = this.accountingService.addInvoiceExtended.call(
      invoiceExtendedFormModel,
    );
    // }

    result$.pipe(filterTrue(), take(1)).subscribe((result) => {
      this.formService.clear();
      switch (this.source()) {
        case SourceEnum.Component:
          // if (result.id == user.contactId) {
          //   this.userService.getCurrentUser.call();
          // }
          // this.router.navigate(['/Contacts/Contacts/Details', result.id]);
          break;
        case SourceEnum.Modal:
          this.dynamicService.outputFromDynamicComponent(
            this.modalRandomIdentifier(),
            result.toString(),
          );
          break;
        default:
          // this.router.navigate(['/Contacts/Contacts/Details', result.id]);
          break;
      }
    });

    this.formService.clear();
  }

  ngOnDestroy(): void {
    this.accountingService.add.reset();
    this.accountingService.createInstance.reset();
    this.accountingService.getPeriods.reset();
    this.accountingService.getAccountByContactId.reset();
    this.applicationFeeRuleService.getByContractId.reset();
    this.accountingService.addInvoiceExtended.reset();
    // this.accountingService.getById.reset();
  }

  onFileEmitterProof(file: File | undefined) {
    this.invoiceForm.patchValue({
      paymentProofFile: file,
    });
  }
}
